"use strict";

import { Loader } from "@googlemaps/js-api-loader";
import { MarkerClusterer } from "@googlemaps/markerclusterer";

interface FetchProps {
    url: string;
    options: object;
    timeout: number;
}

interface Marker {
    initCluster: boolean;
    metadata: Metadata;
}

interface InfoWindow {
    metadata: Metadata;
}

interface Metadata {
    address: string;
    address1: string;
    brands: string[];
    city: string;
    continent: string;
    country: string;
    description: string;
    email: string;
    latitude: string;
    longitude: string;
    phone: string;
    state: string;
    title: string;
    url: string;
    zipCode: string;
    uid: number;
}

export class FilteredMap {
    map: HTMLElement = document.querySelector(".map-display");

    markers = [];
    infoWindow = null;
    mapInstance = null;
    metadata = [];

    TIMEOUT = 200;
    MAX_TIMEOUT = 2000;
    MAXZOOM = 16;
    TOGGLE_STATE = false;

    MARKER_ICON = {
        path: `M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75
               7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38
               0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5
               2.5-1.12 2.5-2.5 2.5z`,
        anchor: {
            x: 14,
            y: 21,
        },
        scale: 2,
        fillColor: "#FB810E",
        fillOpacity: 1,
        strokeColor: "#D66A03",
        strokeWeight: 0.666,
    };

    CLUSTER_LABEL = {
        text: "",
        color: "white",
        fontSize: "18px",
        fontWeight: "bold",
    };

    CLUSTER_ICON = `<svg fill="#155CE6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
        <circle cx="120" cy="120" opacity="1" r="70" />
        <circle cx="120" cy="120" opacity=".3" r="90" />
    </svg>`;

    LAT_REGEX = /^\(?[+-]?(90(\.0+)?|[1-8]?\d(\.\d+)?)$/;
    LNG_REGEX = /^\s?[+-]?(180(\.0+)?|1[0-7]\d(\.\d+)?|\d{1,2}(\.\d+)?)\)?$/;

    constructor() {
        if (!this.map) return;
        this.updateMap();
        this.setEventListeners();
        this.setFilterLabels();
    }

    /**
     * Updates the map with filtered markers
     */
    private updateMap = async () => {
        await this.fetchData();
        this.validateMarkers();
        await this.initMap();
        this.updateBounds();
    };

    /**
     * Initializes the map
     */
    private initMap = async () => {
        const loader = new Loader({
            apiKey: `AIzaSyD7_CNAgRE9WLGys8gZDBp9tfokaobrewc`,
            version: "weekly",
        });

        await loader.load();

        // See: https://developers.google.com/maps/documentation/javascript/reference/map?hl=en#MapOptions
        this.mapInstance = new google.maps.Map(this.map, {
            clickableIcons: false,
            controlSize: 40,
            fullscreenControl: false,
            keyboardShortcuts: true,
            mapTypeControl: false,
            rotateControl: true,
            scaleControl: true,
            streetViewControl: false,
            zoomControl: true,
        });

        this.clearMarkers();

        this.infoWindow = new google.maps.InfoWindow({
            content: "",
            maxWidth: 300,
        });

        this.addMarkers();
    };

    /**
     * Fetches the marker data from back-end
     */
    private fetchData = async () => {
        const url = this.makeSearchQuery();
        const response: Response = await this.fetchWithTimeout({
            url: url,
            options: {
                method: "GET",
                mode: "same-origin",
                cache: "no-cache",
                headers: {
                    "Content-Type": "application/json",
                },
                redirect: "follow",
                referrerPolicy: "no-referrer",
            },
            timeout: 8000,
        });

        if (response.ok || response.status === 200) {
            const data = await response.json();
            this.metadata = data.locations;
        } else {
            this.metadata = [];
        }
    };

    /**
     * Adds timeout handling to fetch command
     * See: https://dmitripavlutin.com/timeout-fetch-request/
     * @return response
     */
    private fetchWithTimeout = async (props: FetchProps) => {
        const { url, options, timeout } = props;
        const controller = new AbortController();
        const id = setTimeout(() => controller.abort(), timeout);
        const response = await fetch(url, {
            ...options,
            signal: controller.signal,
        });
        clearTimeout(id);
        return response;
    };

    /**
     * Clears all markers on the map
     */
    private clearMarkers = () => {
        for (const marker of this.markers) {
            marker.setMap(null);
        }
        this.markers = [];
    };

    /**
     * Adds all markers to the map
     * Initializes the cluster with the last marker
     */
    private addMarkers = () => {
        let initCluster = false;
        const count = this.metadata.length;
        const lastMarker = count - 1;

        if (count !== 0) {
            for (const [index, metadata] of this.metadata.entries()) {
                if (index === lastMarker) {
                    initCluster = true;
                }
                this.addMarker({
                    metadata: metadata,
                    initCluster: initCluster,
                });
            }
        }
    };

    /**
     * Adds a single marker to the map
     * With timeout and DROP animation
     */
    private addMarker = (props: Marker) => {
        const { metadata, initCluster } = props;

        const marker = new google.maps.Marker({
            position: {
                lat: parseFloat(metadata.latitude),
                lng: parseFloat(metadata.longitude),
            },
            map: this.mapInstance,
            animation: google.maps.Animation.DROP,
            title: metadata.title,
            icon: this.MARKER_ICON,
        });

        // See: https://developers.google.com/maps/documentation/javascript/reference/info-window#InfoWindow
        marker.addListener("click", () => {
            this.infoWindow.setContent(
                this.buildInfoWindow({
                    metadata: metadata,
                })
            );
            this.infoWindow.open({
                anchor: marker,
                map: this.mapInstance,
                shouldFocus: true,
            });
        });

        this.markers.push(marker);

        // init cluster with last marker
        // running from here because of setTimeout
        if (initCluster === true) {
            this.initCluster();
        }
    };

    /**
     * Initializes the cluster
     * See: https://developers.google.com/maps/documentation/javascript/marker-clustering
     */
    private initCluster = () => {
        let label = this.CLUSTER_LABEL;
        const icon = window.btoa(this.CLUSTER_ICON);

        const renderer = {
            render({ count, position }) {
                label = Object.assign(label, {
                    text: String(count),
                });
                return new google.maps.Marker({
                    position,
                    icon: {
                        url: `data:image/svg+xml;base64,${icon}`,
                        scaledSize: new google.maps.Size(60, 60),
                        anchor: {
                            x: 30,
                            y: 30,
                        },
                    },
                    label: label,
                    title: `Cluster of ${count} markers`,
                    zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                });
            },
        };

        new MarkerClusterer({
            map: this.mapInstance,
            markers: this.markers,
            renderer: renderer,
        });
    };

    /**
     * Updates the bounds/zoom of the map based on
     * the filtered selection of marker locations
     * See: https://developers.google.com/maps/documentation/javascript/reference/coordinates#LatLngBounds
     */
    private updateBounds = () => {
        const noResultsMessage = document.querySelector(".map-no-results");
        if (noResultsMessage) {
            if (this.metadata.length === 0) {
                noResultsMessage.classList.remove("hidden");
            } else {
                noResultsMessage.classList.add("hidden");
            }
        }

        if (this.metadata.length !== 0) {
            const bounds = new window.google.maps.LatLngBounds();
            this.metadata.map((metadata: Metadata) => {
                bounds.extend({
                    lat: parseFloat(metadata.latitude),
                    lng: parseFloat(metadata.longitude),
                });
            });
            this.mapInstance.setCenter(bounds.getCenter());
            this.mapInstance.fitBounds(bounds);
            const zoom = this.mapInstance.getZoom() || this.MAXZOOM;
            if (zoom > this.MAXZOOM) {
                this.mapInstance.setZoom(this.MAXZOOM);
            }
        } else {
            this.mapInstance.setCenter(new google.maps.LatLng(0, 0));
            this.mapInstance.setZoom(2);
        }
    };

    /**
     * Builds HTML for infoWindow
     * @return string
     */
    private buildInfoWindow = (props: InfoWindow) => {
        const { metadata } = props;

        const infoMarkup = [];

        if (metadata.title) {
            infoMarkup.push(`<p class="title">${metadata.title}</p>`);
        }
        if (metadata.country) {
            infoMarkup.push(`<p class="country">${metadata.country}</p>`);
        }
        if (metadata.address) {
            const markup = [`<p class="address">${metadata.address}`];
            if (metadata.address1) {
                markup.push(`<br/>${metadata.address1}`);
            }
            if (metadata.zipCode) {
                markup.push(`<br/>${metadata.zipCode}`);
            }
            if (metadata.city) {
                markup.push(`<br/>${metadata.city}`);
            }
            if (metadata.state) {
                markup.push(`<br/>${metadata.state}`);
            }
            markup.push(`</p>`);
            infoMarkup.push(markup.join(""));
        }
        if (metadata.phone) {
            infoMarkup.push(
                `<a class="phone" href="tel:${metadata.phone}">${metadata.phone}</a>`
            );
        }
        if (metadata.email) {
            infoMarkup.push(
                `<a class="email" href="mailto:${metadata.email}">${metadata.email}</a>`
            );
        }
        if (metadata.brands) {
            infoMarkup.push(
                `<p class="description">${metadata.brands.join(", ")}</p>`
            );
        }
        if (metadata.url) {
            infoMarkup.push(
                `<a class="cta" href="${this.markAsCountryChoice(
                    metadata.url
                )}" target="_blank">More information</a>`
            );
        }
        return infoMarkup.join("");
    };

    /**
     * Marks internal map links as an explicit country choice (VLQ-467).
     * A click on a country point is as deliberate as the language selector,
     * so the LanguageDetectionMiddleware persists the target locale as the
     * visitor's preference and strips the parameter again with a redirect.
     * External distributor sites are left untouched.
     */
    private markAsCountryChoice = (url: string): string => {
        const origin = window.location.origin;
        if (!url.startsWith("/") && !url.startsWith(`${origin}/`)) {
            return url;
        }

        const [base, hash] = url.split("#");
        const separator = base.includes("?") ? "&" : "?";
        return `${base}${separator}manual_selection=1${hash ? `#${hash}` : ""}`;
    };

    /**
     * Makes the search query based on checked filters
     * @return string
     */
    private makeSearchQuery = () => {
        const checkedInputs: NodeListOf<HTMLInputElement> =
            document.querySelectorAll(
                '.map-filter input[type="checkbox"]:checked'
            );

        const query = new URLSearchParams([["type", "3401"]]);

        if (checkedInputs) {
            for (const checkbox of checkedInputs) {
                query.append(checkbox.name, checkbox.value);
            }
        }

        return window.location.href.replace(/\/+$/, '') + "?" + query.toString();
    };

    /**
     * Sets eventlisteners for checkboxes, ‘Clear filters’,
     * ‘Filter’ toggle and ‘Filters’ toggle all
     */
    private setEventListeners = () => {
        // adds click event to all checkboxes
        const checkboxes: NodeListOf<HTMLInputElement> =
            document.querySelectorAll('.map-filter input[type="checkbox"]');
        if (checkboxes) {
            for (const checkbox of checkboxes) {
                checkbox.addEventListener("click", () => {
                    this.updateMap();
                });
            }
        }

        // adds click event to ‘Clear filters’ button
        const clearFilters = document.querySelector("[data-clear-filters]");
        if (clearFilters) {
            clearFilters.addEventListener("click", () => {
                const checkedInputs: NodeListOf<HTMLInputElement> =
                    document.querySelectorAll(
                        '.map-filter input[type="checkbox"]:checked'
                    );
                if (checkedInputs) {
                    for (const checkbox of checkedInputs) {
                        checkbox.checked = false;
                    }
                }
                this.updateMap();
            });
        }

        // adds click event to toggle a single ‘Filter’
        const filters: NodeListOf<HTMLElement> = document.querySelectorAll(
            ".map-filter-properties [data-filter-type]"
        );
        if (filters) {
            for (const filter of filters) {
                filter.addEventListener("click", () => {
                    if (filter.hasAttribute("aria-expanded")) {
                        filter.removeAttribute("aria-expanded");
                    } else {
                        filter.setAttribute("aria-expanded", "true");
                    }
                });
            }
        }

        // adds click event to ‘Filters’ to toggle all filters
        const toggleFilters: HTMLElement | null = document.querySelector(
            "[data-toggle-filters]"
        );
        if (toggleFilters && filters) {
            toggleFilters.addEventListener("click", () => {
                if (this.TOGGLE_STATE) {
                    for (const filter of filters) {
                        if (filter.hasAttribute("aria-expanded")) {
                            filter.removeAttribute("aria-expanded");
                        }
                    }
                    this.TOGGLE_STATE = false;
                } else {
                    for (const filter of filters) {
                        filter.setAttribute("aria-expanded", "true");
                    }
                    this.TOGGLE_STATE = true;
                }
            });
        }
    };

    /**
     * Sets label of specific filter to a different string
     */
    private setFilterLabels = () => {
        const countries: HTMLElement | null = document.querySelector(
            '[data-filter="distributor_countries"] button span'
        );
    };

    /**
     * Validates latitude and longitude values
     */
    private validateLatLng = (props: Metadata) => {
        const { latitude, longitude } = props;
        if (latitude && longitude) {
            return (
                this.LAT_REGEX.test(latitude) && this.LNG_REGEX.test(longitude)
            );
        }
    };

    private validateMarkers = () => {
        for (const [index, metadata] of this.metadata.entries()) {
            if (!this.validateLatLng(metadata)) {
                this.metadata[index].latitude = "40.00000";
                this.metadata[index].longitude = "-40.00000";
            }
        }
    };
}
